import { motion } from "framer-motion";
import { Radio, Cog, Package, Globe } from "lucide-react";
import { Reveal, staggerContainer, staggerItem } from "./Reveal";
import { PARTNER_HEADING, PARTNER_BODY, PARTNER_BRANDS } from "@/data/content";

const ICONS = { Radio, Cog, Package };

// Reusable "Distribution & Partner Brands" block — used on the Homepage and the
// Products & Services page. Pass a distinct `eyebrow` per placement.
export const DistributionSection = ({
  eyebrow = "Beyond Our Own Manufacturing",
  id,
}) => {
  return (
    <section
      id={id}
      data-testid="distribution-section"
      className="relative bg-aero-surface"
    >
      <div className="mx-auto max-w-[1400px] px-5 py-20 sm:px-8 sm:py-28">
        <Reveal>
          <div className="relative overflow-hidden rounded-2xl border border-saffron/25 bg-aero-navy/50 p-8 sm:p-12">
            {/* accent top rule */}
            <div className="absolute inset-x-0 top-0 h-1 bg-saffron" />
            <div className="blueprint-grid absolute inset-0 opacity-20" />

            <div className="relative">
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-full border border-saffron/40 text-saffron">
                  <Globe className="h-5 w-5" />
                </span>
                <span className="font-heading text-xs uppercase tracking-[0.28em] text-saffron">
                  {eyebrow}
                </span>
              </div>

              <h2 className="mt-6 max-w-3xl font-heading text-3xl font-bold leading-[1.1] tracking-tight text-ink sm:text-4xl">
                {PARTNER_HEADING}
              </h2>
              <p className="mt-5 max-w-2xl font-body text-base leading-relaxed text-steel sm:text-lg">
                {PARTNER_BODY}
              </p>

              <motion.div
                variants={staggerContainer}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                className="mt-10 grid gap-5 sm:grid-cols-3"
              >
                {PARTNER_BRANDS.map((b) => {
                  const Icon = ICONS[b.icon] || Package;
                  return (
                    <motion.div
                      key={b.id}
                      variants={staggerItem}
                      data-testid={`partner-card-${b.id}`}
                      className="group rounded-xl border border-steel/20 bg-aero-deep p-6 transition-all duration-500 hover:-translate-y-1 hover:border-saffron/60"
                    >
                      <span className="grid h-12 w-12 place-items-center rounded-lg border border-steel/30 bg-aero-navy text-saffron transition-colors duration-500 group-hover:border-saffron">
                        <Icon className="h-5 w-5" />
                      </span>
                      <h3 className="mt-5 font-heading text-lg font-semibold text-ink">
                        {b.title}
                      </h3>
                      <p className="mt-2 font-body text-sm leading-relaxed text-steel">
                        {b.blurb}
                      </p>
                    </motion.div>
                  );
                })}
              </motion.div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
